import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  GoodsReceipt,
  GoodsReceiptMutationUpdateArgs,
  GoodsReceiptUpdateFailure,
  GoodsReceiptUpdateInput,
  GoodsReceiptUpdateSuccess,
  UpdateGoodsReceiptMutation,
} from "@/graphql/codegen/graphql"
import { UpdateGoodsReceiptDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpdateGoodsReceiptProps {
  onSuccess?: () => void
  successMessage?: string
}

interface UseUpdateGoodsReceiptReturn {
  update: (input: GoodsReceiptUpdateInput) => Promise<GoodsReceipt>
  isUpdating: boolean
  error?: CombinedError
}

export const useUpdateGoodsReceipt = ({
  onSuccess,
  successMessage = "Advanced Goods Receipt successfully updated.",
}: UseUpdateGoodsReceiptProps = {}): UseUpdateGoodsReceiptReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<
    UpdateGoodsReceiptMutation,
    GoodsReceiptMutationUpdateArgs
  >(UpdateGoodsReceiptDocument)

  const update = async (input: GoodsReceiptUpdateInput): Promise<GoodsReceipt> => {
    const { data, error } = await executeMutation({ input })
    const output = data?.goodsReceipt.update
    const hasError = error || output?.__typename === "GoodsReceiptUpdateFailure"

    if (hasError) {
      const errorMessage =
        error?.message ?? (output as GoodsReceiptUpdateFailure).error ?? "Failed to update goods receipt."
      notify({
        type: NotificationType.Error,
        message: errorMessage,
      })
      throw new Error(errorMessage)
    }

    notify({
      type: NotificationType.Success,
      message: successMessage,
    })
    onSuccess?.()
    return (output as GoodsReceiptUpdateSuccess).goodsReceipt
  }

  return {
    update,
    isUpdating: fetching,
    error,
  }
}
